/**
 * 产品管理 API 服务（领域/产品/版本，复用 fm_ 表）
 * 前缀: /api/v1/file-management
 *
 * 个人空间分组管理复用 project-management 模块的 projectGroupService
 * （/project-groups 接口），本文件不再提供个人分组 CRUD。
 */

import { apiClient } from '@/shared/services/api/client'
import type {
  Domain,
  Product,
  ProductVersion,
  PaginatedVersions,
  CreateVersionRequest,
  UpdateVersionRequest,
  VersionMutationResponse,
  TreeNode,
  CreateChildNodeRequest,
} from '../types'

const BASE = '/api/v1/file-management'

export const productService = {
  /** 领域列表（昇腾/鲲鹏等） */
  async listDomains(): Promise<Domain[]> {
    const r = await apiClient.get(`${BASE}/domains`)
    return r.data
  },

  /** 侧边栏分组列表（领域 + 当前用户个人空间入口，show_in_sidebar=true） */
  async listSidebarGroups(): Promise<{ id: number; name: string; scope: string }[]> {
    const r = await apiClient.get(`${BASE}/domain-tree/sidebar-groups`)
    return r.data
  },

  /** 新建领域（仅管理员） */
  async createDomain(name: string, description?: string | null): Promise<Domain> {
    const r = await apiClient.post(`${BASE}/domains`, { name, description: description ?? null })
    return r.data
  },

  /** 领域下产品（下拉/自动补全用） */
  async listProducts(domainId: number): Promise<Product[]> {
    const r = await apiClient.get(`${BASE}/domain-tree/products`, { params: { domain_id: domainId } })
    return r.data
  },

  /** 指定产品下版本（level=2，parent_project_id=产品） */
  async listVersionsByProduct(productPid: string): Promise<ProductVersion[]> {
    const r = await apiClient.get(`${BASE}/products/${encodeURIComponent(productPid)}/versions`)
    return r.data
  },

  /** 分页扁平列表（合并单元格用） */
  async listVersions(params: {
    page: number
    page_size: number
    domain_id?: number
    keyword?: string
  }): Promise<PaginatedVersions> {
    const r = await apiClient.get(`${BASE}/domain-tree/versions`, { params })
    return r.data
  },

  /** 新增产品/版本（按名幂等） */
  async createVersion(data: CreateVersionRequest): Promise<VersionMutationResponse> {
    const r = await apiClient.post(`${BASE}/domain-tree/versions`, data)
    return r.data
  },

  /** 编辑版本 */
  async updateVersion(versionPid: string, data: UpdateVersionRequest): Promise<VersionMutationResponse> {
    const r = await apiClient.put(`${BASE}/domain-tree/versions/${encodeURIComponent(versionPid)}`, data)
    return r.data
  },

  /** 删除版本（复用 projects.delete_project，带权限校验+级联删资源） */
  async deleteVersion(versionPid: string): Promise<void> {
    await apiClient.delete(`${BASE}/projects/${encodeURIComponent(versionPid)}`)
  },

  /** 删除产品（复用 projects.delete_project，带权限校验+级联删资源） */
  async deleteProduct(productPid: string): Promise<void> {
    await apiClient.delete(`${BASE}/projects/${encodeURIComponent(productPid)}`)
  },

  /** 完整目录树（基于 projects 接口，传 group_id 走树查询，scope 由后端从 group.scope 判断） */
  async getFullTree(params: { group_id?: number | null }): Promise<TreeNode[]> {
    const r = await apiClient.get(`${BASE}/projects`, {
      params: { ...(params.group_id != null ? { group_id: params.group_id } : {}) },
    })
    return r.data
  },

  /** 在领域/个人空间下新建产品（level=1） */
  async createProduct(domainId: number, name: string, description?: string, webhook?: any, git_config?: any, source_project_id?: string, include_children?: boolean): Promise<TreeNode> {
    const r = await apiClient.post(`${BASE}/domains/${domainId}/products`, { name, description, webhook, git_config, source_project_id, include_children })
    return r.data
  },

  /** 在产品下新建版本（level=2） */
  async createVersionUnderProduct(productPid: string, name: string, description?: string, webhook?: any, git_config?: any): Promise<TreeNode> {
    const r = await apiClient.post(`${BASE}/products/${encodeURIComponent(productPid)}/versions`, { name, description, webhook, git_config })
    return r.data
  },

  /** 在版本/文件夹下新建子节点（level≥3，domain_tree 统一接口，带同名校验） */
  async createChildNode(data: CreateChildNodeRequest): Promise<any> {
    const r = await apiClient.post(`${BASE}/domain-tree/nodes`, data)
    return r.data
  },

  /** 删除节点（复用 projects.delete_project，级联删所有资源：文件/文档/分块/向量） */
  async deleteNode(projectPid: string): Promise<void> {
    await apiClient.delete(`${BASE}/projects/${encodeURIComponent(projectPid)}`)
  },
}
